/**
 * Date formatting utilities to avoid timezone issues
 * 
 * IMPORTANT: Always use these utilities to prevent off-by-one date errors
 * caused by timezone conversion.
 */

/**
 * Format a date string or ISO timestamp to YYYY-MM-DD format
 * Handles both date strings and ISO timestamps without timezone conversion
 * 
 * @param {string} dateString - Date string in YYYY-MM-DD or ISO format (YYYY-MM-DDTHH:MM:SS.sssZ)
 * @returns {string} Formatted date string in YYYY-MM-DD format, or empty string if invalid
 * 
 * @example
 * formatDate('2026-01-03') // '2026-01-03'
 * formatDate('2026-01-03T00:00:00.000Z') // '2026-01-03'
 * formatDate(null) // ''
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  
  // If it's an ISO timestamp (contains 'T'), split and take date part
  if (typeof dateString === 'string' && dateString.includes('T')) {
    return dateString.split('T')[0];
  }
  
  // If it's already in YYYY-MM-DD format, return as-is
  return dateString;
};

/**
 * Get today's date in YYYY-MM-DD format without timezone conversion
 * 
 * @returns {string} Today's date in YYYY-MM-DD format
 * 
 * @example
 * getTodayDate() // '2026-01-03'
 */
export const getTodayDate = () => {
  const today = new Date();
  return today.getFullYear() + '-' +
    String(today.getMonth() + 1).padStart(2, '0') + '-' +
    String(today.getDate()).padStart(2, '0');
};

/**
 * Format a date for display with more readable format (e.g., "Jan 3, 2026")
 * Still avoids timezone conversion by working with the string directly
 * 
 * @param {string} dateString - Date string in YYYY-MM-DD or ISO format
 * @returns {string} Formatted date like "Jan 3, 2026" or empty string if invalid
 * 
 * @example
 * formatDateLong('2026-01-03') // 'Jan 3, 2026'
 * formatDateLong('2026-01-03T00:00:00.000Z') // 'Jan 3, 2026'
 */
export const formatDateLong = (dateString) => {
  if (!dateString) return '';
  
  // Extract YYYY-MM-DD part
  const datePart = formatDate(dateString);
  if (!datePart) return '';
  
  // Parse manually to avoid timezone issues
  const [year, month, day] = datePart.split('-').map(Number);
  
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  return `${monthNames[month - 1]} ${day}, ${year}`;
};

/**
 * Calculate duration in days between two dates
 * 
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {number} Number of days between dates (inclusive), or 0 if invalid
 * 
 * @example
 * calculateDuration('2026-01-01', '2026-01-05') // 5
 */
export const calculateDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  
  const start = new Date(formatDate(startDate) + 'T00:00:00');
  const end = new Date(formatDate(endDate) + 'T00:00:00');
  
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays + 1; // Inclusive of both start and end date
};

/**
 * Validate if a date string is in valid YYYY-MM-DD format
 * 
 * @param {string} dateString - Date string to validate
 * @returns {boolean} True if valid YYYY-MM-DD format
 * 
 * @example
 * isValidDate('2026-01-03') // true
 * isValidDate('2026-1-3') // false (not padded)
 * isValidDate('01/03/2026') // false (wrong format)
 */
export const isValidDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return false;
  
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) return false;
  
  // Check if it's a valid date
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  
  return date.getFullYear() === year &&
         date.getMonth() === month - 1 &&
         date.getDate() === day;
};

/**
 * Compare two dates
 * 
 * @param {string} date1 - First date in YYYY-MM-DD format
 * @param {string} date2 - Second date in YYYY-MM-DD format
 * @returns {number} Negative if date1 < date2, 0 if equal, positive if date1 > date2
 * 
 * @example
 * compareDates('2026-01-01', '2026-01-03') // negative number
 * compareDates('2026-01-03', '2026-01-03') // 0
 * compareDates('2026-01-05', '2026-01-03') // positive number
 */
export const compareDates = (date1, date2) => {
  const d1 = new Date(formatDate(date1) + 'T00:00:00');
  const d2 = new Date(formatDate(date2) + 'T00:00:00');
  return d1 - d2;
};

export default {
  formatDate,
  getTodayDate,
  formatDateLong,
  calculateDuration,
  isValidDate,
  compareDates
};
